import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import moment from "moment";
import type { Control, UseFormSetValue, UseFormTrigger } from "react-hook-form";
import type { AppDispatch, RootState } from "../../store/store";
import { resetVisitor } from "../../store/slices/visitorSlice";
import { clearAuth } from "../../store/slices/authSlice";
import { touchlessService } from "../../services/api/touchlessService";
import { envService } from "../../services/api/envService";
import { getCookie, deleteAllCookies } from "../../utils/helpers";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useStrings } from "../../hooks/useStrings";
import type { VisitorTypeField } from "../../types/orgConfig";
import type { VisitorFormData } from "../VisitorForm/visitorForm";
import renderFields from "../VisitorForm/renderFields";
import SignOutSuccess from "./SignOutSuccess";
import "./style/signOut.scss";

type SignOutView = "session" | "fields" | "success";

type SignOutFormData = Record<string, unknown>;

const SignOut: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const { themeColors, config: orgConfig } = useAppConfig();
  const S = useStrings();

  const [view, setView] = useState<SignOutView>("session");
  const [isLoading, setIsLoading] = useState(false);
  const [signOutFields, setSignOutFields] = useState<VisitorTypeField[]>([]);
  const [visitDuration, setVisitDuration] = useState("");
  const [capturedOrgName, setCapturedOrgName] = useState("");
  const [capturedVisitorName, setCapturedVisitorName] = useState("");

  const visitId       = getCookie("visitId");
  const orgName       = getCookie("orgName");
  const siteName      = getCookie("siteName");
  const signInDateTime = getCookie("signInDateTime");
  const visitorName   = getCookie("visitorName");
  const hostName      = getCookie("hostName");

  const firstName = visitorName ? visitorName.split(" ")[0] : "";
  const initials = visitorName
    ? visitorName
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { isValid },
  } = useForm<SignOutFormData>({ mode: "onChange" });

  // Open the sign-out page only for an authenticated session that has a visit
  // to close. The visitId cookie outlives the access token (16h vs a session-
  // scoped JWT), so a stale cookie alone must not expose the visitor's details
  // or allow a token-less sign-out.
  useEffect(() => {
    if ((!isAuthenticated || !visitId) && view !== "success") {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, visitId, navigate, view]);

  // End the session once the success screen is shown.
  useEffect(() => {
    if (view === "success") {
      dispatch(clearAuth());
    }
  }, [view, dispatch]);

  // Read signOutFields from the already-fetched orgConfig instead of refetching.
  useEffect(() => {
    const visitorTypeId = getCookie("visitorTypeId");
    if (!visitorTypeId || !orgConfig) return;
    const vt = orgConfig.visitorType?.find(
      (v) => v.id === visitorTypeId && v.status === "ACTIVE",
    );
    if (!vt) {
      console.error(
        `Visitor type does not belong to this site. siteId: ${orgConfig.siteId}, visitorTypeId: ${visitorTypeId}`,
      );
      return;
    }
    const active = (vt.signOutFields || []).filter(
      (f: VisitorTypeField) => f.status === "ACTIVE",
    );
    setSignOutFields(active);
  }, [orgConfig]);

  const calculateDuration = (signInDateStr: string): string => {
    const signIn = moment(signInDateStr, "DD MMM YY hh:mm A");
    const now = moment();
    const mins = now.diff(signIn, "minutes");
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const submitSignOut = async (
    customFields?: VisitorTypeField[],
    formData?: SignOutFormData,
  ) => {
    setIsLoading(true);
    try {
      const duration = signInDateTime ? calculateDuration(signInDateTime) : "";

      // SmartPass geofence validation: capture GPS before allowing checkout
      const smartpassUuid = sessionStorage.getItem("smartpassUuid");
      if (smartpassUuid) {
        if (!navigator.geolocation) {
          toast.error(S.SIGN_OUT.GEOLOCATION_UNAVAILABLE);
          setIsLoading(false);
          return;
        }

        let checkoutLat: number | undefined;
        let checkoutLng: number | undefined;
        try {
          const position = await new Promise<GeolocationPosition>(
            (resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
              });
            },
          );
          checkoutLat = position.coords.latitude;
          checkoutLng = position.coords.longitude;
        } catch {
          toast.error(S.SIGN_OUT.LOCATION_PERMISSION_REQUIRED);
          setIsLoading(false);
          return;
        }

        const geoParams = `?lat=${checkoutLat}&lng=${checkoutLng}`;
        const geoResp = await fetch(
          `${envService.apiUrl}/smartpass/checkout/${smartpassUuid}/complete${geoParams}`,
          { method: "POST", headers: { "Content-Type": "application/json" } },
        );

        if (geoResp.status === 403) {
          const body = await geoResp.json().catch(() => ({}));
          toast.error(body.message || S.SIGN_OUT.MUST_BE_ON_SITE);
          setIsLoading(false);
          return;
        }

        sessionStorage.removeItem("smartpassUuid");
        sessionStorage.removeItem("smartpassLat");
        sessionStorage.removeItem("smartpassLng");
      }

      let visitSignoutCustomFields:
        | { id: string; name: string; orgCustomFieldId: string; value: string }[]
        | undefined;

      if (customFields?.length && formData) {
        visitSignoutCustomFields = customFields.map((f, i) => {
          const fieldKey = f.fid || f.id || f.name || `field-${i}`;
          return {
            id: f.id,
            name: f.name,
            orgCustomFieldId: f.orgCustomFieldId!,
            value: String(formData[fieldKey] || ""),
          };
        });
      }

      await touchlessService.postSignOutInfo(visitId!, visitSignoutCustomFields);

      setCapturedOrgName(orgName || "");
      setCapturedVisitorName(visitorName || "");
      setVisitDuration(duration);
      setView("success");
      deleteAllCookies();
      dispatch(resetVisitor());
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || S.SIGN_OUT.TOAST_ERROR_FALLBACK;
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = () => {
    if (signOutFields.length > 0) {
      setView("fields");
    } else {
      submitSignOut();
    }
  };

  // "Not you?" — abandon this visit: clear cookies, visitor state, and the
  // session token, then return home.
  const handleStartOver = () => {
    deleteAllCookies();
    dispatch(resetVisitor());
    dispatch(clearAuth());
    navigate("/", { replace: true });
  };

  if (view === "success") {
    return (
      <div className="screen-signout">
        <SignOutSuccess
          visitDuration={visitDuration}
          visitorName={capturedVisitorName}
          orgName={capturedOrgName}
        />
      </div>
    );
  }

  if (view === "fields") {
    return (
      <div className="screen-signout screen-signout--fields">
        <div className="signout-content">
          <div className="signout-heading signout-heading--left">
            <div className="signout-eyebrow">
              {S.SIGN_OUT.CUSTOM_FIELDS_EYEBROW}
            </div>
            <h1 className="signout-heading--left--confirm-screen-title">
              {S.SIGN_OUT.CUSTOM_FIELDS_TITLE}
            </h1>
            <p className="signout-fields-subtitle">
              {S.SIGN_OUT.CUSTOM_FIELDS_SUBTITLE}
            </p>
          </div>

          <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
            <div className="sign-out-form signout-fields-list">
              {signOutFields.map((field, i) => (
                <div key={field.id} className="form-group">
                  <Form.Label
                    className={field.setting === "MANDATORY" ? "required-field" : ""}
                  >
                    {field.displayText || field.name}
                  </Form.Label>
                  {renderFields(
                    field,
                    i,
                    control as unknown as Control<VisitorFormData, unknown>,
                    watch() as VisitorFormData,
                    setValue as unknown as UseFormSetValue<VisitorFormData>,
                    trigger as unknown as UseFormTrigger<VisitorFormData>,
                    () => {},
                    () => {},
                    themeColors.btnColor,
                    {},
                    [],
                    false,
                    "",
                    () => [],
                    orgConfig!,
                    null,
                    S,
                  )}
                </div>
              ))}
            </div>

            <div className="signout-actions">
              <button
                type="button"
                className="btn-signout-primary"
                style={{ backgroundColor: themeColors.btnColor }}
                disabled={isLoading || !isValid}
                onClick={handleSubmit((data) => submitSignOut(signOutFields, data))}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M10.5 11L14 8l-3.5-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <line x1="14" y1="8" x2="6" y2="8" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                {isLoading ? S.SIGN_OUT.BTN_SIGNING_OUT : S.SIGN_OUT.BTN_CONFIRM_SIGN_OUT}
              </button>

              <button
                type="button"
                className="btn-signout-ghost"
                onClick={() => setView("session")}
                disabled={isLoading}
              >
                {S.SIGN_OUT.BTN_GO_BACK}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // view === 'session'
  return (
    <div
      className="screen-signout screen-signout--session"
      data-testid="signout-screen"
    >
      <div className="signout-content">
        <div className="signout-avatar-top" hidden={true}>
          <span
            className="signout-avatar-initials"
            style={{ color: themeColors.btnColor }}
          >
            {initials}
          </span>
        </div>

        <div className="signout-heading" data-testid="signout-heading">
          <div
            className="signout-eyebrow"
            style={{ color: themeColors.btnColor }}
          >
            <span
              className="eyebrow-dot"
              style={{ background: themeColors.btnColor }}
              hidden={true}
            />
            {S.SIGN_OUT.EYEBROW}
          </div>
          <h1 data-testid="signout-title">
            {firstName
              ? `${S.SIGN_OUT.TITLE_WITH_NAME} ${firstName}!`
              : S.SIGN_OUT.TITLE_FALLBACK}
          </h1>
          <p className="signout-session-subtitle">{S.SIGN_OUT.SUBTITLE_SESSION}</p>
        </div>

        <div className="session-block" data-testid="signout-info-strip">
          <div className="session-profile-row">
            <div
              className="session-profile-avatar"
              style={{
                background: themeColors.btnColor + "18",
                color: themeColors.btnColor,
              }}
            >
              {initials}
            </div>
            <div className="session-profile-text">
              <span className="session-profile-name">
                {visitorName || S.SIGN_OUT.GUEST_PLACEHOLDER}
              </span>
              <span className="signed-in-badge">{S.SIGN_OUT.STRIP_SUBTITLE}</span>
            </div>
          </div>

          <div className="session-rows">
            {orgName && (
              <div className="session-row" data-testid="signout-info-org">
                <div className="s-icon s-org">
                  <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                    <rect x="2" y="4" width="13" height="11" rx="1.5" stroke="#7c4dff" strokeWidth="1.4" />
                    <path d="M6 15V10h5v5" stroke="#7c4dff" strokeWidth="1.3" strokeLinecap="round" />
                    <path d="M2 7.5h13" stroke="#7c4dff" strokeWidth="1.3" />
                    <path d="M8.5 2v2" stroke="#7c4dff" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="s-text">
                  <span className="s-label">{S.SIGN_OUT.LABEL_ORG}</span>
                  <span className="s-value">{orgName}</span>
                </div>
              </div>
            )}

            {siteName && (
              <div className="session-row" data-testid="signout-info-site">
                <div className="s-icon s-loc">
                  <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                    <path d="M8.5 2C6 2 4 4 4 6.5c0 3.8 4.5 8.5 4.5 8.5s4.5-4.7 4.5-8.5C13 4 11 2 8.5 2z" stroke="#059669" strokeWidth="1.4" />
                    <circle cx="8.5" cy="6.5" r="1.6" stroke="#059669" strokeWidth="1.3" />
                  </svg>
                </div>
                <div className="s-text">
                  <span className="s-label">{S.SIGN_OUT.LABEL_LOCATION}</span>
                  <span className="s-value">{siteName}</span>
                </div>
              </div>
            )}

            {hostName && (
              <div className="session-row" data-testid="signout-info-host">
                <div className="s-icon s-host">
                  <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                    <circle cx="8.5" cy="6" r="3" stroke="#2563eb" strokeWidth="1.4" />
                    <path d="M2.5 15c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="#2563eb" strokeWidth="1.4" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="s-text">
                  <span className="s-label">{S.SIGN_OUT.LABEL_HOST}</span>
                  <span className="s-value">{hostName}</span>
                </div>
              </div>
            )}

            <div className="session-row" data-testid="signout-info-time">
              <div className="s-icon s-time">
                <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                  <circle cx="8.5" cy="8.5" r="6" stroke="#d97706" strokeWidth="1.4" />
                  <path d="M8.5 5.5V9l2.2 2.2" stroke="#d97706" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="s-text">
                <span className="s-label">{S.SIGN_OUT.LABEL_SIGNED_IN_AT}</span>
                <span className="s-value">
                  {signInDateTime
                    ? (() => {
                        const parts = signInDateTime.split(" ");
                        const date = parts.slice(0, 3).join(" ");
                        const time = parts.slice(3).join(" ");
                        return time ? `${date}  ·  ${time}` : signInDateTime;
                      })()
                    : ""}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="signout-actions" data-testid="signout-actions">
          <button
            type="button"
            className="btn-signout-primary"
            style={{ backgroundColor: themeColors.btnColor }}
            onClick={handleSignOut}
            disabled={isLoading}
            data-testid="signout-button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M10.5 11L14 8l-3.5-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="14" y1="8" x2="6" y2="8" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {isLoading ? S.SIGN_OUT.BTN_SIGNING_OUT : S.SIGN_OUT.BTN_SIGN_OUT}
          </button>

          <button
            type="button"
            className="btn-signout-not-you"
            onClick={handleStartOver}
            disabled={isLoading}
            data-testid="startover-button"
          >
            <span className="not-you-label">{S.SIGN_OUT.LABEL_NOT_YOU}</span>{" "}
            <span className="not-you-action" style={{ color: themeColors.btnColor }}>
              {S.SIGN_OUT.BTN_START_OVER}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignOut;
